import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink, useRouteMatch} from 'react-router-dom';
import {Navbar, Alignment, Button} from "@blueprintjs/core";
import {IconNames} from '@blueprintjs/icons';

import {useCurrentUser} from '../../../hooks';
import {toggleShowSearch} from '../../../store/actions/searchActions';
import {isAdmin, isLoggedIn} from '../../../utils/auth';
import {isPremise} from '../../../utils/premise';
import {ApiStatus} from '../../../models';
import {IState} from '../../../store/reducers';
import config from '../../../../config';
import {ReportHeaderLeft, ReportHeaderRight} from './report';
import SettingsMenu from './SettingsMenu';
import DefaultLogo from '../../../../assets/logo/logo_light.svg';
import {DummyHeader} from './DummyHeader';
import {useCustomLinks} from "../../../hooks/use-workspace-config";
import {getHeaderClassName} from './utils';

import './Header.scss';

const Header = () => {
  const [logoLoaded, setLogoLoaded] = useState(false);
  const loadingStatus = useSelector(
    (state: IState) => state.users.loadingStatus
  );
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const _isLoggedIn = isLoggedIn(currentUser);
  const isNotebookRoute = useRouteMatch('/r/n/edit/:notebookId'); // report type: `notebook`
  const isReportRoute = useRouteMatch('/r/edit/:reportId'); // report type: `default`
  const isTableauEmbedRoute = useRouteMatch('/r/t/edit/:notebookId'); // report type: tableau_embed
  const showReportHeader = !!(isNotebookRoute || isReportRoute || isTableauEmbedRoute);
  const customLinks = useCustomLinks();
  /*
   * Users on Premise cannot see "Administration" menu
   * TODO: do it more general
   */
  const showAdministrationMenu = !(isPremise() && !isAdmin(currentUser));

  const logoUrl = config.app.logo_url || DefaultLogo;
  const isCustomLogo = !!config.app.logo_url;

  const customerName = config.app.customer_name;

  useEffect(() => {
    if (isCustomLogo) {
      setLogoLoaded(true);
    }
  }, [logoUrl, isCustomLogo]);

  const onSearchClick = () => {
    dispatch(toggleShowSearch());
  };

  if (loadingStatus === ApiStatus.LOADING) {
    return <DummyHeader/>
  }

  return (
    <Navbar className={getHeaderClassName()}>
      <Navbar.Group className="header__navbar-group" align={Alignment.LEFT}>
        <NavLink to="/app" className={`header__${isCustomLogo ? 'custom_logo' : 'logo'}`}>
          {isCustomLogo && logoLoaded ? (
            <img
              src={logoUrl}
              alt={customerName}
              className="header__custom_logo__image"
            />
          ) : (
            <img
              src={DefaultLogo}
              alt={customerName}
              className="header__logo__image"
            />
          )}
        </NavLink>
        {
          _isLoggedIn && !showReportHeader && (
            <>
              <NavLink to="/app" activeClassName='header__active-link'>
                <Button
                  minimal={true}
                  text="Home"
                  title="Home"
                  icon={IconNames.HOME}
                />
              </NavLink>
              <NavLink to="/r" activeClassName='header__active-link'>
                <Button
                  minimal={true}
                  text="Explore"
                  title="Expolore"
                  icon={IconNames.GEOLOCATION}
                />
              </NavLink>
              <NavLink to="/d" activeClassName='header__active-link'>
                <Button
                  minimal={true}
                  text="Data & Integrations"
                  title="Data & Integrations"
                  icon={IconNames.DATA_CONNECTION}
                />
              </NavLink>
              <NavLink to="/a/log" activeClassName='header__active-link'>
                <Button
                  minimal={true}
                  text="Execution Log"
                  title="Execution Log"
                  icon={IconNames.PULSE}
                />
              </NavLink>
              {
                showAdministrationMenu && (
                  <NavLink to="/a/o" activeClassName='header__active-link'>
                    <Button
                      minimal={true}
                      text="Administration"
                      title="Administration"
                      icon={IconNames.COG}/>
                  </NavLink>
                )
              }
              {
                config.premise_features.custom_links && customLinks && customLinks.length > 0 && customLinks.map((link: {name: string, value: string}, index: number) => {
                  return (
                    <a
                      key={`custom-link-${index}`}
                      href={link.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="header__active-link"
                    >
                      <Button
                        minimal={true}
                        text={link.name || "Custom Link"}
                        title={link.name || "Custom Link"}
                        icon={IconNames.LINK}
                      />
                    </a>
                  )
                })
              }
              <Button minimal={true} icon={IconNames.SEARCH} onClick={onSearchClick}/>
            </>
          )
        }
        {
          _isLoggedIn && showReportHeader && <ReportHeaderLeft/>
        }
      </Navbar.Group>
      <Navbar.Group className="header__navbar-group" align={Alignment.RIGHT}>
        {
          _isLoggedIn && showReportHeader && <ReportHeaderRight/>
        }
        {
          _isLoggedIn && <SettingsMenu/>
        }
        {
          !_isLoggedIn && (
            <a
              href={config.app.url + '/login'}
              className='header__active-link'
            >
              <Button
                minimal={false}
                text={`Log In to ${customerName}`}
                icon={IconNames.LOG_IN}
              />
            </a>
          )
        }
      </Navbar.Group>
    </Navbar>
  );
};

export default Header;
